// src/components/work_orders/ViewWorkOrderModal.js
// Enhanced Modal to display Work Order Details, Chat, Parts Used, and SLA/Helpdesk Integrations
//
// --- UPDATE (v2 — AI assistant + FlowSense links):
//   • New "🧠 AI Assistant" tab (and summary strip in the body): urgency score with reasons, next best
//     actions that actually run (accept AI suggestion, assign, escalate, start, follow up, complete,
//     edit, citizen update, open linked asset), data checklist explaining what the AI needs and why.
//   • "Linked in FlowSense" row: asset / road / sensor the job belongs to, opens that page.
//   • "AI draft" resolution notes from the job + parts used (template you edit before saving).
//   • FIX: "Send to Citizen" only showed a success toast — it now calls workOrderService
//     .sendCitizenEmail / .sendCitizenUpdate when available, otherwise copies the draft to the
//     clipboard and says so honestly.
//   • FIX: null status / priority crashed the header; a status not in the dropdown (e.g. 'open')
//     made the select show the wrong value; "Not Configured" never showed for missing SLA deadlines.
//   • Status changes are announced on the FlowSense bus so other pages refresh.
//   • Optional props: onAssign(wo), onEdit(wo), onOpenLink(link).
//   • --- UPDATE: type / domain badge, "Inspections & follow-ups" (parent + linked jobs),
//     create a linked inspection or a follow-up from findings — onCreateLinked(kind, wo)
//     opens the create form pre-filled; without it the modal calls the API directly.
// --- END UPDATE

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'; // --- UPDATE: + useMemo/useCallback/useRef
import PropTypes from 'prop-types';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody,
  ModalCloseButton, Button, VStack, Heading, Divider, SimpleGrid, Flex, Text,
  Tag, useColorModeValue, Box, Spinner, Tabs, TabList, Tab, TabPanels, TabPanel,
  FormControl, FormLabel, Select, Textarea, Collapse, useToast,
  HStack, Icon, Badge, Tooltip, Alert, AlertIcon, AlertDescription,
  ScaleFade, Fade, IconButton, Stat, Progress, StatLabel, StatNumber, StatHelpText
} from '@chakra-ui/react';
import { format, parseISO } from 'date-fns';
import {
  MdPerson, MdCalendarToday, MdAccessTime, MdDescription,
  MdPriorityHigh, MdCheckCircle, MdEdit, MdRefresh, MdSmartToy, MdSensors, MdTrendingUp, MdEmail,
  MdTimer, MdWarning, MdErrorOutline, MdInfo, MdHeadsetMic,
  MdPsychology, MdOutlineAutoAwesome, MdOpenInNew, MdLink, // --- UPDATE
  MdFactCheck, MdAddTask, MdSubdirectoryArrowRight, // --- UPDATE
} from 'react-icons/md';

// Import components
import WorkOrderChat from './WorkOrderChat';
import PartsUsed from './PartsUsed';
import AddInventoryToWorkOrderModal from './AddInventoryToWorkOrderModal';
import workOrderInventoryService from '../../services/workOrderInventoryService';
import * as workOrderService from '../../services/workOrderService';
import AISuggestionPanel from '../ai/AISuggestionPanel';
import AIPartRecommendationPanel from '../ai/AIPartRecommendationPanel';
import WorkOrderFeedback from './WorkOrderFeedback';
// --- UPDATE: AI assistant + cross-page bus
import { WorkOrderAiHelper, WoHint, WoTypeBadge } from './WorkOrderAiHelper';
import { analyzeWorkOrder, draftResolutionNotes, getLinkedEntities, FLOWSENSE_ROUTES } from './workOrderAiGuide';
import { inferType } from './workOrderCatalog';
import { publish as publishBus, BUS_EVENTS } from '../../utils/flowsenseBus';
// --- END UPDATE

// ============================================================================
// HELPER FUNCTIONS & CONFIGS
// ============================================================================

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    return format(parseISO(dateString), 'MMM dd, yyyy');
  } catch (e) {
    return 'Invalid Date';
  }
};

const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    return format(parseISO(dateString), 'MMM dd, yyyy h:mm a');
  } catch (e) {
    return 'Invalid Date';
  }
};

const getStatusColorScheme = (status) => {
  switch (status) {
    case 'pending': return 'yellow';
    case 'scheduled': return 'purple';
    case 'in_progress': return 'blue';
    case 'completed': return 'green';
    case 'cancelled': return 'red';
    case 'on_hold': return 'orange';
    case 'assigned': return 'cyan';
    default: return 'gray';
  }
};

const getPriorityColorScheme = (priority) => {
  switch (priority) {
    case 'low': return 'gray';
    case 'medium': return 'blue';
    case 'high': return 'orange';
    case 'critical': return 'red';
    default: return 'gray';
  }
};

const STATUS_OPTIONS = ['pending', 'scheduled', 'assigned', 'in_progress', 'completed', 'cancelled', 'on_hold'];

// --- NEW: SLA UI Configurations ---
const SLA_STATUS_CONFIG = {
  compliant: { color: 'green', label: 'Compliant', icon: MdCheckCircle },
  at_risk: { color: 'orange', label: 'At Risk', icon: MdWarning },
  breached: { color: 'red', label: 'Breached', icon: MdErrorOutline },
  critical: { color: 'orange', label: 'Critical', icon: MdPriorityHigh },
  met: { color: 'green', label: 'Met', icon: MdCheckCircle },
  no_sla: { color: 'gray', label: 'No SLA', icon: MdInfo }
};

// --- NEW: Dynamic SLA Math for the Work Order Progress Bar ---
const calculateSLA = (wo) => {
  if (!wo.sla_resolution_deadline) return { status: 'no_sla', percentage: 0, text: 'No SLA tracked' };
  
  const now = new Date();
  const due = new Date(wo.sla_resolution_deadline);
  const created = new Date(wo.created_at || now);
  const totalDuration = due.getTime() - created.getTime();
  let elapsed = now.getTime() - created.getTime();

  let status = wo.sla_status || 'compliant';
  let text = '';
  let percentage = 0;

  if (wo.status === 'completed' || wo.status === 'closed') {
      status = 'met';
      if (wo.completed_at) {
          elapsed = new Date(wo.completed_at).getTime() - created.getTime();
      }
      text = 'Resolved';
  } else if (now > due) {
      status = 'breached';
      elapsed = totalDuration; 
      const over = Math.floor((now.getTime() - due.getTime()) / 60000);
      text = `Overdue by ${Math.floor(over/60)}h ${over%60}m`;
  } else {
      const rem = Math.floor((due.getTime() - now.getTime()) / 60000);
      text = `${Math.floor(rem/60)}h ${rem%60}m left`;
      if (rem < 60) status = 'critical';
  }

  if (totalDuration > 0) {
      percentage = Math.min(100, Math.max(0, Math.round((elapsed / totalDuration) * 100)));
  }

  return { status, percentage, text };
};
// -------------------------------------------------------------

// ============================================================================
// MAIN COMPONENT
// ============================================================================

function ViewWorkOrderModal({ isOpen, onClose, workOrder, onUpdate,
  // --- UPDATE: optional AI action routing
  onAssign, onEdit, onOpenLink,
  onCreateLinked,
  // --- END UPDATE
}) {
  // State management
  const [usedItems, setUsedItems] = useState([]);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [isAddPartsModalOpen, setIsAddPartsModalOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  
  // AI Email State
  const [draftEmail, setDraftEmail] = useState('');
  const [isGeneratingEmail, setIsGeneratingEmail] = useState(false);
  const [emailError, setEmailError] = useState('');

  const toast = useToast();

  // Theme colors
  const headingColor = useColorModeValue('gray.800', 'white');
  const modalBodyBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const mutedTextColor = useColorModeValue('gray.500', 'gray.400');
  const cardBg = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const labelColor = useColorModeValue('gray.600', 'gray.400');
  const tabBg = useColorModeValue('white', 'gray.800');
  const tabHoverBg = useColorModeValue('gray.50', 'gray.700');
  const aiCardBg = useColorModeValue('purple.50', 'rgba(128, 90, 213, 0.1)'); 
  const emailCardBg = useColorModeValue('blue.50', 'rgba(66, 153, 225, 0.1)');
  // --- UPDATE
  const draftBg = useColorModeValue('white', 'gray.900');
  const aiStripBg = useColorModeValue('linear(to-r, purple.50, blue.50)', 'linear(to-r, rgba(128,90,213,0.15), rgba(49,130,206,0.12))');
  const AI_TAB_INDEX = 4;
  const [busyIntent, setBusyIntent] = useState(null);
  const statusSectionRef = useRef(null);
  // --- UPDATE: linked work (parent / inspections / follow-ups)
  const [linked, setLinked] = useState({ status: 'idle', data: null, error: null });
  const linkedReqRef = useRef(0);
  const modalMountedRef = useRef(true);
  useEffect(() => { modalMountedRef.current = true; return () => { modalMountedRef.current = false; }; }, []);
  const loadLinked = useCallback(async (id) => {
    if (id === undefined || id === null) return;
    const rid = ++linkedReqRef.current;
    setLinked(prev => ({ ...prev, status: 'loading', error: null }));
    try {
      const data = await workOrderService.getLinkedWorkOrders(id);
      if (!modalMountedRef.current || rid !== linkedReqRef.current) return;
      setLinked({ status: 'ok', data, error: null });
    } catch (err) {
      if (!modalMountedRef.current || rid !== linkedReqRef.current) return;
      setLinked({ status: 'error', data: null, error: err.message || 'Could not load linked work' });
    }
  }, []);
  useEffect(() => {
    if (isOpen && workOrder?.id !== undefined && workOrder?.id !== null) loadLinked(workOrder.id);
  }, [isOpen, workOrder?.id, workOrder?.updated_at, loadLinked]);
  // --- END UPDATE
  const aiAnalysis = useMemo(() => (workOrder ? analyzeWorkOrder(workOrder, { usedItemsCount: usedItems.length }) : null), [workOrder, usedItems.length]);
  const statusOptions = useMemo(
    () => (workOrder?.status && !STATUS_OPTIONS.includes(workOrder.status) ? [workOrder.status, ...STATUS_OPTIONS] : STATUS_OPTIONS),
    [workOrder?.status]
  );
  // --- END UPDATE

  // ============================================================================
  // DATA FETCHING
  // ============================================================================

  const fetchUsedItems = async () => {
    if (workOrder?.id) {
      setIsLoadingItems(true);
      try {
        const data = await workOrderInventoryService.getItemsForWorkOrder(workOrder.id);
        setUsedItems(data || []);
      } catch (error) {
        console.error('[ViewWorkOrderModal] Failed to fetch used items:', error);
        toast({
          title: 'Error Loading Inventory',
          description: 'Could not load parts used for this work order.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setIsLoadingItems(false);
      }
    }
  };

  // --- UPDATE: reset only when a different work order opens. Live updates replace the
  //   workOrder object; resetting on every object change threw the user back to the Messages tab.
  useEffect(() => {
    if (isOpen && workOrder) {
      fetchUsedItems();
      setNewStatus(workOrder.status);
      setResolutionNotes(workOrder.resolution_notes || ''); 
      setActiveTab(0); 
      setDraftEmail(''); 
      setEmailError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, workOrder?.id]);
  // Follow live status changes unless the user has already picked a new status
  const lastServerStatusRef = useRef(workOrder?.status);
  useEffect(() => {
    if (!workOrder) return;
    setNewStatus(prev => (prev === lastServerStatusRef.current ? workOrder.status : prev));
    lastServerStatusRef.current = workOrder.status;
  }, [workOrder?.status]); // eslint-disable-line react-hooks/exhaustive-deps
  // --- END UPDATE

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  const handlePartsModalClose = () => {
    setIsAddPartsModalOpen(false);
    fetchUsedItems();
  };

  const handleSuggestionUpdate = () => {
    fetchUsedItems();
    if (onUpdate) onUpdate();
  };

  const handleStatusUpdate = async () => {
    if ((newStatus === 'completed' || newStatus === 'closed') && !resolutionNotes.trim()) {
      toast({
        title: 'Resolution Notes Required',
        description: 'Please provide resolution notes when completing a work order.',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsUpdatingStatus(true);
    try {
      await workOrderService.updateWorkOrderStatus(
        workOrder.id,
        newStatus,
        resolutionNotes.trim() || undefined
      );
      
      toast({
        title: 'Status Updated Successfully',
        description: `Work order status changed to ${newStatus.replace(/_/g, ' ')}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      // --- UPDATE: let other pages know (asset histories, utilities, roads)
      publishBus(BUS_EVENTS.WORK_ORDER_CHANGED, { action: 'status', id: workOrder.id, status: newStatus, asset_id: workOrder.asset_id ?? null, road_id: workOrder.road_id ?? null });
      if (onUpdate) onUpdate({ ...workOrder, status: newStatus, resolution_notes: resolutionNotes.trim() || workOrder.resolution_notes });
      // --- END UPDATE
      onClose();
      
    } catch (error) {
      console.error('[ViewWorkOrderModal] Status update error:', error);
      toast({
        title: 'Update Failed',
        description: error.message || 'Could not update work order status.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleRefreshItems = () => {
    fetchUsedItems();
  };

  const handleGenerateEmailDraft = async () => {
      setIsGeneratingEmail(true);
      setEmailError('');
      try {
          const draft = await workOrderService.generateCitizenEmailDraft(workOrder.id);
          setDraftEmail(draft);
          toast({
              title: 'Draft Generated',
              description: 'AI has successfully drafted the citizen update.',
              status: 'success',
              duration: 3000,
              isClosable: true,
          });
      } catch (error) {
          console.error('[ViewWorkOrderModal] Failed to generate AI draft:', error);
          setEmailError('Failed to generate draft. Ensure local AI is running.');
      } finally {
          setIsGeneratingEmail(false);
      }
  };

  // --- UPDATE: real send (or honest clipboard fallback)
  // --- UPDATE: real send via workOrderService.sendCitizenUpdate; clipboard fallback when the
  // job has no linked citizen (or sending fails) so the draft is never lost
  const handleSendCitizenEmail = async () => {
    if (!draftEmail.trim()) return;
    try {
      const res = await workOrderService.sendCitizenUpdate(workOrder.id, draftEmail);
      const via = Array.isArray(res?.channels) && res.channels.length ? ` via ${res.channels.join(' + ')}` : '';
      toast({ title: 'Update Sent', description: `The update has been sent to the citizen${via}.`, status: 'success', duration: 3000 });
      setDraftEmail('');
      publishBus(BUS_EVENTS.WORK_ORDER_CHANGED, { action: 'citizen_update', id: workOrder.id, asset_id: workOrder.asset_id ?? null });
    } catch (error) {
      let copied = false;
      try { await navigator.clipboard?.writeText?.(draftEmail); copied = !!navigator.clipboard; } catch { /* ignore */ }
      const noChannel = error?.code === 'NO_CITIZEN_CHANNEL';
      toast({
        title: noChannel ? 'No citizen linked to this job' : 'Send Failed',
        description: `${error.message || 'Could not send the update.'}${copied ? ' The draft was copied to your clipboard.' : ''}`,
        status: noChannel ? 'warning' : 'error', duration: 6000, isClosable: true,
      });
    }
  };
  // --- END UPDATE

  // --- UPDATE: AI assistant actions
  const handleAiAction = useCallback(async (intent, action = {}) => {
    if (!workOrder) return;
    const run = async (fn, okMsg) => {
      setBusyIntent(intent);
      try {
        await fn();
        if (okMsg) toast({ title: okMsg, status: 'success', duration: 3000, isClosable: true });
        publishBus(BUS_EVENTS.WORK_ORDER_CHANGED, { action: intent.toLowerCase(), id: workOrder.id, asset_id: workOrder.asset_id ?? null });
        onUpdate?.(workOrder);
      } catch (err) {
        toast({ title: 'Action failed', description: err.message, status: 'error', duration: 5000, isClosable: true });
      } finally {
        setBusyIntent(null);
      }
    };
    switch (intent) {
      case 'ACCEPT_SUGGESTION':
        return run(() => workOrderService.acceptSuggestion(workOrder.id), `Assigned to ${workOrder.suggested_technician_name || 'technician'}`);
      case 'ASSIGN':
        if (onAssign) return onAssign(workOrder);
        return toast({ title: 'Assign from the work order list', status: 'info', duration: 3000 });
      case 'ESCALATE':
        if (typeof workOrderService.updateWorkOrder === 'function' && workOrder.priority !== 'critical') {
          return run(() => workOrderService.updateWorkOrder(workOrder.id, { priority: 'critical' }), 'Escalated to critical priority');
        }
        if (onEdit) return onEdit(workOrder);
        return toast({ title: 'Escalate', description: 'Raise the priority in Edit and notify the supervisor in Messages.', status: 'info', duration: 4000 });
      case 'START':
        return run(() => workOrderService.updateWorkOrderStatus(workOrder.id, 'in_progress'), 'Marked as in progress');
      case 'FOLLOW_UP':
        return setActiveTab(0);
      case 'COMPLETE':
        setNewStatus('completed');
        if (!resolutionNotes.trim()) setResolutionNotes(draftResolutionNotes(workOrder, usedItems));
        setTimeout(() => statusSectionRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' }), 50);
        return undefined;
      case 'EDIT':
        if (onEdit) return onEdit(workOrder);
        setTimeout(() => statusSectionRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' }), 50);
        return undefined;
      case 'CITIZEN_UPDATE':
        return handleGenerateEmailDraft();
      // --- UPDATE: inspections ↔ work orders
      case 'CREATE_INSPECTION':
      case 'CREATE_FOLLOW_UP': {
        const kind = intent === 'CREATE_INSPECTION' ? 'inspection' : 'follow_up';
        if (onCreateLinked) return onCreateLinked(kind, workOrder);
        return run(async () => {
          if (kind === 'inspection') await workOrderService.createLinkedInspection(workOrder.id, {});
          else await workOrderService.createFollowUpWorkOrder(workOrder.id, {});
          loadLinked(workOrder.id);
        }, kind === 'inspection' ? 'Linked inspection created' : 'Follow-up work order created');
      }
      // --- END UPDATE
      case 'OPEN_LINK': {
        const link = action.link;
        if (!link) return undefined;
        if (onOpenLink) return onOpenLink(link);
        window.location.assign(link.href);
        return undefined;
      }
      default:
        return undefined;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workOrder, onAssign, onEdit, onOpenLink, onUpdate, resolutionNotes, usedItems, toast, onCreateLinked, loadLinked]);
  // --- END UPDATE

  // ============================================================================
  // RENDER GUARDS
  // ============================================================================

  if (!workOrder) {
    return null;
  }

  // Calculate dynamic SLA stats for the UI
  const slaStats = calculateSLA(workOrder);
  const SlaIcon = SLA_STATUS_CONFIG[slaStats.status]?.icon || MdInfo;

  // Extract AI data
  const techSuggestion = workOrder?.suggested_technician_name
    ? {
        name: workOrder.suggested_technician_name,
        email: workOrder.suggested_technician_email,
        id: workOrder.suggested_technician_id
      }
    : null;

  const dispatchNotes = workOrder?.ai_dispatch_notes || null;
  
  let predictionContext = null;
  if (workOrder.prediction_context) {
      try {
          predictionContext = typeof workOrder.prediction_context === 'string' 
            ? JSON.parse(workOrder.prediction_context) 
            : workOrder.prediction_context;
      } catch (e) { console.warn('Failed to parse prediction context'); }
  }

  const hasStatusChanged = newStatus !== workOrder.status;

  // ============================================================================
  // RENDER HELPERS
  // ============================================================================

  const DetailRow = ({ label, value, icon, fullWidth = false }) => (
    <Flex
      gridColumn={fullWidth ? '1 / -1' : 'auto'}
      align="flex-start"
      gap={2}
    >
      {icon && (
        <Icon
          as={icon}
          color={mutedTextColor}
          boxSize={4}
          mt={0.5}
        />
      )}
      <Box flex={1}>
        <Text fontSize="xs" fontWeight="600" color={labelColor} mb={0.5}>
          {label}
        </Text>
        <Box color={textColor} fontSize="sm">
          {value}
        </Box>
      </Box>
    </Flex>
  );

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="6xl"
        scrollBehavior="inside"
        isCentered
      >
        <ModalOverlay backdropFilter="blur(4px)" />
        <ScaleFade in={isOpen} initialScale={0.95}>
          <ModalContent bg={modalBodyBg} maxH="90vh">
            {/* Header */}
            <ModalHeader borderBottomWidth="1px" borderColor={borderColor} pb={4}>
              <Flex justify="space-between" align="center">
                <Box>
                  <Heading size="md" color={headingColor}>
                    Work Order Details
                  </Heading>
                  <HStack spacing={3} mt={1}>
                    <Text fontSize="sm" color={mutedTextColor} fontFamily="mono">
                      ID: {workOrder.id}
                    </Text>
                    <WoTypeBadge workOrder={workOrder} />{/* --- UPDATE */}
                    {/* --- NEW: Linked Helpdesk Badge --- */}
                    {workOrder.source === 'helpdesk' && (
                       <Badge 
                         colorScheme="teal" 
                         variant="subtle" 
                         cursor="pointer" 
                         // --- UPDATE: '/helpdesk' was not a route; open the ticket / report this job came from
                         onClick={() => {
                           const hd = getLinkedEntities(workOrder).find(l => l.kind === 'helpdesk');
                           handleAiAction('OPEN_LINK', { link: hd || { kind: 'helpdesk', id: workOrder.id, label: 'Helpdesk', href: FLOWSENSE_ROUTES.helpdeskForWorkOrder(workOrder.id) } });
                         }}
                         // --- END UPDATE
                         title="Open Helpdesk"
                       >
                         <HStack spacing={1}><Icon as={MdHeadsetMic}/> <Text>Linked Helpdesk Ticket</Text></HStack>
                       </Badge>
                    )}
                  </HStack>
                </Box>
                <VStack align="flex-end" spacing={2}>
                    <HStack spacing={2}>
                    {(workOrder.source === 'prediction' || workOrder.source === 'ai_prescriptive') && (
                        <Tag size="lg" colorScheme="purple" borderRadius="full" px={3}>
                            <Icon as={MdSmartToy} mr={2} /> AI Predicted
                        </Tag>
                    )}
                    {workOrder.source === 'ai_sensor_alert' && (
                        <Tag size="lg" colorScheme="orange" borderRadius="full" px={3}>
                            <Icon as={MdSensors} mr={2} /> Sensor Alert
                        </Tag>
                    )}
                    
                    <Tag
                        size="lg"
                        colorScheme={getStatusColorScheme(workOrder.status)}
                        borderRadius="full"
                        px={4}
                    >
                        {String(workOrder.status || 'unknown').replace(/_/g, ' ').toUpperCase()}
                    </Tag>
                    <Badge
                        colorScheme={getPriorityColorScheme(workOrder.priority)}
                        fontSize="sm"
                        px={3}
                        py={1}
                        borderRadius="md"
                    >
                        {String(workOrder.priority || 'unset').toUpperCase()}
                    </Badge>
                    </HStack>

                    {/* --- NEW: Live SLA Header Progress Indicator --- */}
                    {slaStats.status !== 'no_sla' && (
                        <Tooltip label={`Deadline: ${formatDateTime(workOrder.sla_resolution_deadline)}`} hasArrow>
                            <HStack spacing={2} bg={cardBg} px={3} py={1} borderRadius="full" borderWidth="1px" borderColor={borderColor}>
                                <Icon as={SlaIcon} color={`${SLA_STATUS_CONFIG[slaStats.status].color}.500`} />
                                <Progress
                                    value={slaStats.percentage}
                                    size="sm"
                                    colorScheme={SLA_STATUS_CONFIG[slaStats.status].color}
                                    w="80px"
                                    borderRadius="full"
                                />
                                <Text fontSize="xs" fontWeight="bold" color={mutedTextColor}>
                                    {slaStats.text}
                                </Text>
                            </HStack>
                        </Tooltip>
                    )}
                    {/* ------------------------------------------------ */}
                </VStack>
              </Flex>
            </ModalHeader>

            <ModalCloseButton />

            {/* Body */}
            <ModalBody py={6}>
              <VStack spacing={6} align="stretch">
                
                {/* --- UPDATE: AI summary strip */}
                {aiAnalysis && (
                  <Flex p={3} gap={3} align="center" wrap="wrap" borderRadius="lg" bgGradient={aiStripBg} role="region" aria-label="AI summary">
                    <Icon as={MdPsychology} color="purple.400" boxSize={5} />
                    <Badge colorScheme={aiAnalysis.level.color} variant="solid">{aiAnalysis.closed ? 'Resolved' : `${aiAnalysis.score} · ${aiAnalysis.level.label}`}</Badge>
                    <Text fontSize="sm" flex={1} minW="200px" noOfLines={2}>{aiAnalysis.summary}</Text>
                    {aiAnalysis.actions[0] && aiAnalysis.actions[0].intent !== 'OPEN_LINK' && (
                      <Button size="sm" colorScheme="purple" leftIcon={<Icon as={MdOutlineAutoAwesome} />}
                        isLoading={busyIntent === aiAnalysis.actions[0].intent}
                        onClick={() => handleAiAction(aiAnalysis.actions[0].intent, aiAnalysis.actions[0])}>
                        {aiAnalysis.actions[0].label.length > 34 ? `${aiAnalysis.actions[0].label.slice(0, 32)}…` : aiAnalysis.actions[0].label}
                      </Button>
                    )}
                    <Button size="sm" variant="outline" colorScheme="purple" onClick={() => setActiveTab(AI_TAB_INDEX)}>AI Assistant</Button>
                  </Flex>
                )}
                {/* --- END UPDATE */}

                {/* Prediction Details Card */}
                {predictionContext && (
                    <Fade in={true}>
                        <Box bg={aiCardBg} p={4} borderRadius="lg" borderWidth="1px" borderColor="purple.200">
                            <HStack mb={2}>
                                <Icon as={MdTrendingUp} color="purple.500" />
                                <Heading size="sm" color="purple.700">AI Prediction Insights</Heading>
                            </HStack>
                            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                                {predictionContext.riskScore && (
                                    <Stat size="sm">
                                        <StatLabel>Risk Score</StatLabel>
                                        <StatNumber>{predictionContext.riskScore}/100</StatNumber>
                                        <StatHelpText>Failure Probability</StatHelpText>
                                    </Stat>
                                )}
                                {predictionContext.healthStatus && (
                                    <Stat size="sm">
                                        <StatLabel>Asset Health</StatLabel>
                                        <StatNumber>{predictionContext.healthStatus.toUpperCase()}</StatNumber>
                                    </Stat>
                                )}
                                {predictionContext.predictedFailureDate && (
                                    <Stat size="sm">
                                        <StatLabel>Predicted Failure</StatLabel>
                                        <StatNumber>{formatDate(predictionContext.predictedFailureDate)}</StatNumber>
                                    </Stat>
                                )}
                                {predictionContext.estimatedCost && (
                                    <Stat size="sm">
                                        <StatLabel>Est. Cost Savings</StatLabel>
                                        <StatNumber>${predictionContext.estimatedCost}</StatNumber>
                                    </Stat>
                                )}
                            </SimpleGrid>
                        </Box>
                    </Fade>
                )}

                {/* Work Order Information Card */}
                <Fade in={true}>
                  <Box
                    bg={cardBg}
                    p={5}
                    borderRadius="lg"
                    borderWidth="1px"
                    borderColor={borderColor}
                  >
                    <Heading size="sm" color={headingColor} mb={4}>
                      Information
                    </Heading>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      <DetailRow
                        label="Title"
                        value={<Text fontWeight="semibold">{workOrder.title}</Text>}
                        icon={MdDescription}
                      />
                      <DetailRow
                        label="Assigned To"
                        value={
                          workOrder.assigned_user_username ? (
                            <HStack spacing={2}>
                              <Icon as={MdPerson} boxSize={4} />
                              <Text>{workOrder.assigned_user_username}</Text>
                            </HStack>
                          ) : (
                            <Text color={mutedTextColor} fontStyle="italic">
                              Unassigned
                            </Text>
                          )
                        }
                      />
                      
                      {/* --- UPDATE: Show Both Standard Due Date and Strict SLA Deadline --- */}
                      <DetailRow
                        label="Scheduled Due Date"
                        value={formatDate(workOrder.due_date)}
                        icon={MdCalendarToday}
                      />
                      <DetailRow
                        label={<>SLA Resolution Deadline<WoHint metric="sla" /></>}
                        value={
                          <Text color={slaStats.status === 'breached' ? 'red.500' : textColor} fontWeight={slaStats.status === 'breached' ? 'bold' : 'normal'}>
                              {workOrder.sla_resolution_deadline ? formatDateTime(workOrder.sla_resolution_deadline) : 'Not Configured'}
                          </Text>
                        }
                        icon={MdTimer}
                      />
                      {/* ----------------------------------------------------------------- */}

                      <DetailRow
                        label="Created At"
                        value={formatDateTime(workOrder.created_at)}
                        icon={MdAccessTime}
                      />
                      <DetailRow
                        label="Last Updated"
                        value={formatDateTime(workOrder.updated_at)}
                        icon={MdAccessTime}
                      />
                      {/* --- UPDATE: linked FlowSense entities */}
                      <DetailRow
                        label="Linked in FlowSense"
                        icon={MdLink}
                        value={getLinkedEntities(workOrder).length ? (
                          <HStack spacing={2} flexWrap="wrap">
                            {getLinkedEntities(workOrder).map(l => (
                              <Button key={`${l.kind}-${l.id}`} size="xs" variant="outline" colorScheme="teal" rightIcon={<Icon as={MdOpenInNew} />}
                                onClick={() => handleAiAction('OPEN_LINK', { link: l })}>
                                {l.label}
                              </Button>
                            ))}
                          </HStack>
                        ) : <Text color="orange.400" fontSize="sm">Not linked — link an asset or road so this repair feeds its history.</Text>}
                      />
                      {/* --- END UPDATE */}
                      {/* --- UPDATE: inspections & follow-ups */}
                      <DetailRow
                        label={inferType(workOrder) === 'inspection' ? 'Inspected job & follow-ups' : 'Inspections & follow-ups'}
                        icon={MdFactCheck}
                        fullWidth
                        value={
                          <VStack align="stretch" spacing={2} role="region" aria-label="Linked work">
                            {linked.status === 'loading' && !linked.data && <HStack><Spinner size="xs" /><Text fontSize="sm" color={mutedTextColor}>Loading linked work…</Text></HStack>}
                            {linked.status === 'error' && (
                              <HStack><Text fontSize="sm" color="orange.400">{linked.error}</Text><Button size="xs" variant="ghost" onClick={() => loadLinked(workOrder.id)}>Retry</Button></HStack>
                            )}
                            {linked.data && linked.data.supported === false && (
                              <Text fontSize="xs" color={mutedTextColor}>Linking is not enabled on the database yet (run the work order links migration).</Text>
                            )}
                            {linked.data?.parent && (
                              <Button size="xs" variant="ghost" justifyContent="flex-start" leftIcon={<Icon as={MdSubdirectoryArrowRight} transform="scaleX(-1)" />}
                                onClick={() => handleAiAction('OPEN_LINK', { link: { kind: 'work_order', id: linked.data.parent.id, label: linked.data.parent.title, href: FLOWSENSE_ROUTES.workOrder(linked.data.parent.id) } })}>
                                {inferType(workOrder) === 'inspection' ? 'Inspection of' : 'Raised from'}: {linked.data.parent.title} ({String(linked.data.parent.status || '').replace(/_/g, ' ')})
                              </Button>
                            )}
                            {(linked.data?.children || []).map(c => (
                              <HStack key={c.id} spacing={2}>
                                <Badge colorScheme={c.work_order_type === 'inspection' ? 'cyan' : 'orange'} fontSize="2xs">{c.work_order_type === 'inspection' ? 'Inspection' : 'Follow-up'}</Badge>
                                <Button size="xs" variant="link" colorScheme="teal"
                                  onClick={() => handleAiAction('OPEN_LINK', { link: { kind: 'work_order', id: c.id, label: c.title, href: FLOWSENSE_ROUTES.workOrder(c.id) } })}>
                                  {c.title}
                                </Button>
                                <Tag size="sm" colorScheme={getStatusColorScheme(c.status)}>{String(c.status || '').replace(/_/g, ' ')}</Tag>
                              </HStack>
                            ))}
                            {linked.data?.supported && !linked.data?.parent && !(linked.data?.children || []).length && (
                              <Text fontSize="xs" color={mutedTextColor}>No linked inspections or follow-ups yet.</Text>
                            )}
                            <HStack spacing={2} flexWrap="wrap">
                              {inferType(workOrder) !== 'inspection' && (
                                <Button size="xs" colorScheme="cyan" variant="outline" leftIcon={<Icon as={MdFactCheck} />} isLoading={busyIntent === 'CREATE_INSPECTION'}
                                  onClick={() => handleAiAction('CREATE_INSPECTION')}>Create linked inspection</Button>
                              )}
                              {inferType(workOrder) === 'inspection' && (
                                <Button size="xs" colorScheme="orange" variant="outline" leftIcon={<Icon as={MdAddTask} />} isLoading={busyIntent === 'CREATE_FOLLOW_UP'}
                                  onClick={() => handleAiAction('CREATE_FOLLOW_UP')}>Raise follow-up from findings</Button>
                              )}
                            </HStack>
                          </VStack>
                        }
                      />
                      {/* --- END UPDATE */}
                      {workOrder.description && (
                        <DetailRow
                          label="Description"
                          value={
                            <Text whiteSpace="pre-wrap" lineHeight="1.6">
                              {workOrder.description}
                            </Text>
                          }
                          fullWidth
                        />
                      )}
                    </SimpleGrid>
                  </Box>
                </Fade>

                {/* AI Suggestion Panel */}
                {(['pending', 'open'].includes(workOrder?.status)) && !workOrder.assigned_user_id && techSuggestion && (
                  <Fade in={true}>
                    <AISuggestionPanel
                      suggestion={techSuggestion}
                      notes={dispatchNotes}
                      workOrderId={workOrder.id}
                      onAssignmentSuccess={() => {
                        if (onUpdate) onUpdate();
                        onClose();
                      }}
                    />
                  </Fade>
                )}

                {/* Tabbed Content */}
                <Box
                  bg={tabBg}
                  borderRadius="lg"
                  borderWidth="1px"
                  borderColor={borderColor}
                  overflow="hidden"
                >
                  <Tabs
                    index={activeTab}
                    onChange={setActiveTab}
                    variant="soft-rounded"
                    colorScheme="blue"
                  >
                    <TabList
                      px={4}
                      pt={4}
                      pb={2}
                      borderBottomWidth="1px"
                      borderColor={borderColor}
                      gap={2}
                    >
                      <Tab
                        fontSize="sm"
                        fontWeight="medium"
                        _selected={{ bg: 'blue.500', color: 'white' }}
                        _hover={{ bg: tabHoverBg }}
                      >
                        💬 Messages
                      </Tab>
                      <Tab
                        fontSize="sm"
                        fontWeight="medium"
                        _selected={{ bg: 'blue.500', color: 'white' }}
                        _hover={{ bg: tabHoverBg }}
                      >
                        📦 Inventory
                      </Tab>
                      <Tab
                        fontSize="sm"
                        fontWeight="medium"
                        _selected={{ bg: 'blue.500', color: 'white' }}
                        _hover={{ bg: tabHoverBg }}
                      >
                        ✨ AI Parts
                      </Tab>
                      <Tab
                        fontSize="sm"
                        fontWeight="medium"
                        _selected={{ bg: 'blue.500', color: 'white' }}
                        _hover={{ bg: tabHoverBg }}
                      >
                        ⭐ Feedback
                      </Tab>
                      {/* --- UPDATE */}
                      <Tab
                        fontSize="sm"
                        fontWeight="medium"
                        _selected={{ bg: 'purple.500', color: 'white' }}
                        _hover={{ bg: tabHoverBg }}
                      >
                        🧠 AI Assistant
                      </Tab>
                      {/* --- END UPDATE */}
                    </TabList>

                    <TabPanels>
                      {/* Messages Tab */}
                      <TabPanel p={0}>
                        {workOrder.id ? (
                          <WorkOrderChat workOrderId={workOrder.id} />
                        ) : (
                          <Alert status="warning" borderRadius="md" m={4}>
                            <AlertIcon />
                            <AlertDescription>
                              Cannot load messages without a Work Order ID.
                            </AlertDescription>
                          </Alert>
                        )}
                      </TabPanel>

                      {/* Inventory Tab */}
                      <TabPanel p={4}>
                        {isLoadingItems ? (
                          <Flex justify="center" align="center" minH="200px">
                            <VStack spacing={3}>
                              <Spinner size="lg" color="blue.500" thickness="3px" />
                              <Text color={mutedTextColor} fontSize="sm">
                                Loading inventory...
                              </Text>
                            </VStack>
                          </Flex>
                        ) : (
                          <Box>
                            <Flex justify="space-between" align="center" mb={4}>
                              <Heading size="sm" color={headingColor}>
                                Parts Used
                              </Heading>
                              <Tooltip label="Refresh inventory">
                                <IconButton
                                  icon={<Icon as={MdRefresh} />}
                                  size="sm"
                                  variant="ghost"
                                  onClick={handleRefreshItems}
                                  aria-label="Refresh"
                                />
                              </Tooltip>
                            </Flex>
                            <PartsUsed
                              workOrder={workOrder}
                              usedItems={usedItems}
                              onAddParts={() => setIsAddPartsModalOpen(true)}
                            />
                          </Box>
                        )}
                      </TabPanel>

                      {/* AI Parts Tab */}
                      <TabPanel p={4}>
                        <AIPartRecommendationPanel
                          workOrderId={workOrder.id}
                          onUpdate={handleSuggestionUpdate}
                        />
                      </TabPanel>

                      {/* Feedback Tab */}
                      <TabPanel p={4}>
                        <WorkOrderFeedback workOrder={workOrder} />
                      </TabPanel>

                      {/* --- UPDATE: AI assistant */}
                      <TabPanel p={4}>
                        <WorkOrderAiHelper workOrder={workOrder} usedItemsCount={usedItems.length} onAction={handleAiAction} busyIntent={busyIntent} />
                      </TabPanel>
                      {/* --- END UPDATE */}
                    </TabPanels>
                  </Tabs>
                </Box>

                {/* Status Update Section */}
                <Box
                  ref={statusSectionRef} // --- UPDATE
                  bg={cardBg}
                  p={5}
                  borderRadius="lg"
                  borderWidth="1px"
                  borderColor={borderColor}
                >
                  <Flex justify="space-between" align="center" mb={4}>
                    <Heading size="sm" color={headingColor}>
                      Update Status
                    </Heading>
                    {hasStatusChanged && (
                      <Badge colorScheme="blue" fontSize="xs">
                        Modified
                      </Badge>
                    )}
                  </Flex>

                  <VStack spacing={4} align="stretch">
                    <FormControl>
                      <FormLabel fontSize="sm" fontWeight="medium" color={labelColor}>
                        New Status
                      </FormLabel>
                      <Select
                        value={newStatus}
                        onChange={(e) => setNewStatus(e.target.value)}
                        size="md"
                      >
                        {statusOptions.map((status) => ( /* --- UPDATE: includes current status */
                          <option key={status} value={status}>
                            {status.replace(/_/g, ' ').toUpperCase()}
                          </option>
                        ))}
                      </Select>
                    </FormControl>

                    <Collapse in={newStatus === 'completed' || newStatus === 'closed'} animateOpacity>
                      <FormControl isRequired>
                        <Flex justify="space-between" align="center">
                          <FormLabel fontSize="sm" fontWeight="medium" color={labelColor}>
                            Resolution Notes<WoHint field="resolution" />
                          </FormLabel>
                          {/* --- UPDATE: template from the job + parts used */}
                          <Button size="xs" variant="ghost" colorScheme="purple" leftIcon={<Icon as={MdOutlineAutoAwesome} />}
                            onClick={() => setResolutionNotes(draftResolutionNotes(workOrder, usedItems))}>
                            AI draft
                          </Button>
                        </Flex>
                        <Textarea
                          placeholder="Describe the work performed, parts used, and how the issue was resolved..."
                          value={resolutionNotes}
                          onChange={(e) => setResolutionNotes(e.target.value)}
                          rows={4}
                          resize="vertical"
                        />
                        <Text fontSize="xs" color={mutedTextColor} mt={1}>
                          Required when marking as completed
                        </Text>
                      </FormControl>
                    </Collapse>

                    {hasStatusChanged && (
                      <Alert status="info" borderRadius="md" fontSize="sm">
                        <AlertIcon />
                        <AlertDescription>
                          Click "Save Status" below to apply the changes.
                        </AlertDescription>
                      </Alert>
                    )}
                  </VStack>
                </Box>

                {/* AI Citizen Communication Section */}
                {/* Only visible when status is or was completed/closed */}
                {(workOrder.status === 'completed' || workOrder.status === 'closed') && (
                  <Fade in={true}>
                    <Box
                      bg={emailCardBg}
                      p={5}
                      borderRadius="lg"
                      borderWidth="1px"
                      borderColor="blue.200"
                    >
                      <Flex justify="space-between" align="center" mb={4}>
                        <HStack>
                            <Icon as={MdEmail} color="blue.500" boxSize={5} />
                            <Heading size="sm" color="blue.800">
                                Citizen Communication
                            </Heading>
                        </HStack>
                      </Flex>

                      {!draftEmail ? (
                          <Button
                              onClick={handleGenerateEmailDraft}
                              isLoading={isGeneratingEmail}
                              loadingText="AI is drafting..."
                              colorScheme="blue"
                              variant="outline"
                              w="full"
                              leftIcon={<Icon as={MdSmartToy} />}
                          >
                              Auto-Draft Citizen Update
                          </Button>
                      ) : (
                          <VStack align="stretch" spacing={3}>
                              <Box position="relative">
                                  <Badge colorScheme="blue" position="absolute" top="-2" right="-2" zIndex="1">
                                      AI Draft
                                  </Badge>
                                  <Textarea
                                      value={draftEmail}
                                      onChange={(e) => setDraftEmail(e.target.value)}
                                      rows={6}
                                      bg={draftBg}
                                      borderColor="blue.200"
                                      _hover={{ borderColor: "blue.300" }}
                                      _focus={{ borderColor: "blue.500", boxShadow: "outline" }}
                                  />
                              </Box>
                              <HStack justify="flex-end">
                                  <Button 
                                      size="sm" 
                                      variant="ghost" 
                                      colorScheme="gray" 
                                      onClick={() => setDraftEmail('')}
                                  >
                                      Discard
                                  </Button>
                                  <Button 
                                      size="sm" 
                                      colorScheme="green"
                                      onClick={handleSendCitizenEmail} // --- UPDATE: real send / honest fallback
                                  >
                                      Send to Citizen
                                  </Button>
                              </HStack>
                          </VStack>
                      )}
                      
                      {emailError && (
                          <Text color="red.500" fontSize="sm" mt={2}>
                              {emailError}
                          </Text>
                      )}
                    </Box>
                  </Fade>
                )}

              </VStack>
            </ModalBody>

            {/* Footer */}
            <ModalFooter borderTopWidth="1px" borderColor={borderColor}>
              <HStack spacing={3}>
                <Button
                  variant="ghost"
                  onClick={onClose}
                  isDisabled={isUpdatingStatus}
                >
                  Cancel
                </Button>
                <Button
                  colorScheme="blue"
                  onClick={handleStatusUpdate}
                  isLoading={isUpdatingStatus}
                  loadingText="Updating..."
                  isDisabled={!hasStatusChanged}
                  leftIcon={<Icon as={MdCheckCircle} />}
                >
                  Save Status
                </Button>
              </HStack>
            </ModalFooter>
          </ModalContent>
        </ScaleFade>
      </Modal>

      {/* Add Parts Modal */}
      {isAddPartsModalOpen && (
        <AddInventoryToWorkOrderModal
          isOpen={isAddPartsModalOpen}
          onClose={handlePartsModalClose}
          workOrder={workOrder}
          // --- UPDATE: parts change cost + stock elsewhere (Inventory, cost reports)
          onSaved={(items) => { publishBus(BUS_EVENTS.WORK_ORDER_CHANGED, { action: 'parts_logged', id: workOrder.id, asset_id: workOrder.asset_id ?? null, count: items?.length || 0 }); onUpdate?.(workOrder); }}
          // --- END UPDATE
        />
      )}
    </>
  );
}

// ============================================================================
// PROP TYPES
// ============================================================================

ViewWorkOrderModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  workOrder: PropTypes.object,
  onUpdate: PropTypes.func,
  // --- UPDATE
  onAssign: PropTypes.func,
  onEdit: PropTypes.func,
  onOpenLink: PropTypes.func,
  onCreateLinked: PropTypes.func, // --- UPDATE: (kind: 'inspection' | 'follow_up', workOrder) => void
};

export default ViewWorkOrderModal;